import { useState, useEffect, useCallback } from "react";
import {
  Plus, Trash2, Search, X, Check,
  AlertTriangle, Layers, Filter, Loader2, Eye, ZoomIn, Pencil,
} from "lucide-react";
import Pagination, { usePagination } from "../shared/Pagination";
import { useCustomizedBanners } from "../context/CustomizedBannersContext";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const toActive = (status) =>
  typeof status === "string"
    ? status.toLowerCase() === "active"
    : status === 1 || status === true;

const toStatus = (active) => (active ? "active" : "inactive");

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success"
              ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "warning"
              ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"}`}
        >
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL PRIMITIVES
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  const isSidePanel = maxW !== "max-w-sm";
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  if (isSidePanel) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col overflow-hidden">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-gray-100`}>{children}</div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between">
      <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
      <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
        <X size={16} className="text-white" />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STATUS TOGGLE
───────────────────────────────────────────── */
function StatusToggle({ active, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0 disabled:opacity-50
        ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}
    >
      <span
        className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${active ? "translate-x-5" : "translate-x-1"}`}
      />
    </button>
  );
}

/* ─────────────────────────────────────────────
   VIEW MODAL
───────────────────────────────────────────── */
function ViewModal({ open, onClose, item, getImageUrl }) {
  if (!item) return null;
  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg">
      <ModalHeader title="Customization Banner Preview" icon={Eye} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div className="rounded-xl overflow-hidden border border-[#e8e0d0] bg-gray-50">
          <img
            src={getImageUrl(item.image)}
            alt={item.description}
            className="w-full object-cover max-h-64"
            onError={(e) => { e.target.src = "https://placehold.co/600x200?text=No+Image"; }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-gray-800">{item.description}</p>
            <span
              className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full
                ${toActive(item.status)
                  ? "bg-green-50 text-green-700 border border-green-200"
                  : "bg-red-50 text-red-500 border border-red-200"}`}
            >
              {toActive(item.status) ? "Active" : "Inactive"}
            </span>
          </div>
          {/* <p className="text-xs text-gray-400">Banner ID: {item.id}</p> */}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
        <button
          onClick={onClose}
          className="px-5 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-white transition-all active:scale-95"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  useEffect(() => {
    if (open) { setLoading(false); setError(""); }
  }, [open]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(item?.id);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to delete banner");
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete Banner?</h3>
        <p className="text-sm text-gray-500 mb-2">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-700">"{item?.description}"</span>? This cannot be undone.
        </p>
        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading
              ? <Loader2 size={14} className="animate-spin" />
              : <><Trash2 size={14} /> Delete</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL
───────────────────────────────────────────── */
// function AddModal({ open, onClose, onSave }) {
//   const [description, setDescription] = useState("");
//   const [active, setActive]           = useState(true);
//   const [file, setFile]               = useState(null);
//   const [preview, setPreview]         = useState(null);
//   const [error, setError]             = useState({});
//   const [loading, setLoading]         = useState(false);

//   useEffect(() => {
//     if (open) {
//       setDescription("");
//       setActive(true);
//       setFile(null);
//       setPreview(null);
//       setError({});
//       setLoading(false);
//     }
//   }, [open]);

//   const handleImageChange = (e) => {
//     const f = e.target.files[0];
//     if (!f) return;
//     setFile(f);
//     const reader = new FileReader();
//     reader.onload = (ev) => setPreview(ev.target.result);
//     reader.readAsDataURL(f);
//     setError((er) => ({ ...er, image: "" }));
//   };

//   const handleSave = async () => {
//     const errs = {};
//     if (!description.trim()) errs.description = "Description is required";
//     if (!file) errs.image = "Banner image is required";
//     if (Object.keys(errs).length) { setError(errs); return; }

//     setLoading(true);
//     try {
//       const fd = new FormData();
//       fd.append("image", file);
//       fd.append("description", description.trim());
//       fd.append("status", toStatus(active));
//       await onSave(fd);
//       onClose();
//     } catch (err) {
//       setError({ general: err.message || "Failed to save banner" });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Modal open={open} onClose={onClose} maxW="max-w-lg">
//       <ModalHeader title="Add Customization Banner" icon={Plus} onClose={onClose} />
//       <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

//         {/* Description */}
//         <div className="flex flex-col gap-1.5">
//           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
//             Description <span className="text-red-400">*</span>
//           </label>
//           <input
//             autoFocus
//             type="text"
//             value={description}
//             onChange={(e) => { setDescription(e.target.value); setError((er) => ({ ...er, description: "" })); }}
//             placeholder="e.g., Monogram Embroidery – Gold"
//             className={`w-full h-10 px-3 rounded-xl border text-sm bg-white text-gray-800 placeholder-gray-400 outline-none transition-all
//               ${error.description ? "border-red-300 ring-2 ring-red-100" : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
//           />
//           {error.description && <p className="text-xs text-red-500">{error.description}</p>}
//         </div>

//         {/* Image Upload */}
//         <div className="flex flex-col gap-1.5">
//           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
//             Image <span className="text-red-400">*</span>
//           </label>
//           <label
//             className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden
//               ${preview ? "h-40" : "h-32"}
//               ${error.image ? "border-red-300 bg-red-50" : "border-[#e2ddd4] bg-white hover:border-teal-400 hover:bg-teal-50/30"}`}
//           >
//             {preview ? (
//               <img src={preview} alt="preview" className="h-full w-full object-cover" />
//             ) : (
//               <>
//                 <Layers size={22} className="text-gray-300" />
//                 <span className="text-xs text-gray-400">Click to upload banner image</span>
//               </>
//             )}
//             <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
//           </label>
//           {error.image && <p className="text-xs text-red-500">{error.image}</p>}
//         </div>

//         {/* Status */}
//         <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-white">
//           <div>
//             <p className="text-sm font-medium text-gray-700">Status</p>
//             <p className="text-xs text-gray-400 mt-0.5">
//               {active ? "This banner is currently active" : "This banner is currently inactive"}
//             </p>
//           </div>
//           <button
//             type="button"
//             onClick={() => setActive((v) => !v)}
//             className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
//               ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}
//           >
//             <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${active ? "translate-x-5" : "translate-x-1"}`} />
//           </button>
//         </div>

//         {error.general && <p className="text-xs text-red-500">{error.general}</p>}
//       </div>

//       <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-100">
//         <button
//           onClick={onClose}
//           disabled={loading}
//           className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
//         >
//           Cancel
//         </button>
//         <button
//           onClick={handleSave}
//           disabled={loading}
//           className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-all active:scale-95 flex items-center gap-2 shadow-sm disabled:opacity-60"
//         >
//           {loading ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Save Banner</>}
//         </button>
//       </div>
//     </Modal>
//   );
// }

/* ─────────────────────────────────────────────
   EDIT MODAL
───────────────────────────────────────────── */
function EditModal({ open, onClose, onSave, item, getImageUrl }) {
  const [description, setDescription] = useState("");
  const [active, setActive]           = useState(true);
  const [file, setFile]               = useState(null);
  const [preview, setPreview]         = useState(null);
  const [error, setError]             = useState({});
  const [loading, setLoading]         = useState(false);

  // Populate fields when item changes
  useEffect(() => {
    if (open && item) {
      setDescription(item.description || "");
      setActive(toActive(item.status));
      setFile(null);
      setPreview(getImageUrl(item.image));
      setError({});
      setLoading(false);
    }
  }, [open, item]);

  const handleImageChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
    setError((er) => ({ ...er, image: "" }));
  };

  const handleSave = async () => {
    const errs = {};
    if (!description.trim()) errs.description = "Description is required";
    if (Object.keys(errs).length) { setError(errs); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("description", description.trim());
      fd.append("status", toStatus(active));
      // Only append image if a new file was selected
      if (file) fd.append("image", file);
      await onSave(item.id, fd);
      onClose();
    } catch (err) {
      setError({ general: err.message || "Failed to update banner" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg">
      <ModalHeader title="Edit Customization Banner" icon={Pencil} onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Description <span className="text-red-400">*</span>
          </label>
          <input
            autoFocus
            type="text"
            value={description}
            onChange={(e) => { setDescription(e.target.value); setError((er) => ({ ...er, description: "" })); }}
            placeholder="e.g., Monogram Embroidery – Gold"
            className={`w-full h-10 px-3 rounded-xl border text-sm bg-white text-gray-800 placeholder-gray-400 outline-none transition-all
              ${error.description ? "border-red-300 ring-2 ring-red-100" : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
          />
          {error.description && <p className="text-xs text-red-500">{error.description}</p>}
        </div>

        {/* Image Upload */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Image
            <span className="ml-1.5 text-[10px] font-normal text-gray-400 normal-case">(leave unchanged to keep current)</span>
          </label>
          <label
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer transition-all overflow-hidden relative
              ${preview ? "h-40" : "h-32"}
              ${error.image ? "border-red-300 bg-red-50" : "border-[#e2ddd4] bg-white hover:border-teal-400 hover:bg-teal-50/30"}`}
          >
            {preview ? (
              <>
                <img src={preview} alt="preview" className="h-full w-full object-cover" />
                {/* overlay hint */}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-all flex items-center justify-center">
                  <span className="opacity-0 hover:opacity-100 text-white text-xs font-medium transition-opacity">
                    Click to change
                  </span>
                </div>
              </>
            ) : (
              <>
                <Layers size={22} className="text-gray-300" />
                <span className="text-xs text-gray-400">Click to upload new image</span>
              </>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
          </label>
          {file && (
            <p className="text-[11px] text-teal-600 flex items-center gap-1">
              <Check size={11} /> New image selected: {file.name}
            </p>
          )}
          {error.image && <p className="text-xs text-red-500">{error.image}</p>}
        </div>

        {/* Status */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-white">
          <div>
            <p className="text-sm font-medium text-gray-700">Status</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {active ? "This banner is currently active" : "This banner is currently inactive"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setActive((v) => !v)}
            className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
              ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}
          >
            <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${active ? "translate-x-5" : "translate-x-1"}`} />
          </button>
        </div>

        {error.general && <p className="text-xs text-red-500">{error.general}</p>}
      </div>

      <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-100">
        <button
          onClick={onClose}
          disabled={loading}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-all active:scale-95 flex items-center gap-2 shadow-sm disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} /> Update Banner</>}
        </button>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   CUSTOMIZATION IMAGE PAGE
───────────────────────────────────────────── */
export default function CustomizationImagePage() {
  const {
    customizedBanners,
    customizedBannersLoading,
    addCustomizedBanner,
    updateCustomizedBanner,
    deleteCustomizedBanner,
    updateCustomizedBannerStatus,
    getCustomizedBannerImageUrl,
  } = useCustomizedBanners();

  const [search, setSearch]             = useState("");
  const [sortBy, setSortBy]             = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filtersOpen, setFiltersOpen]   = useState(false);
  const [addOpen, setAddOpen]           = useState(false);
  const [editItem, setEditItem]         = useState(null);
  const [viewItem, setViewItem]         = useState(null);
  const [delItem, setDelItem]           = useState(null);
  const [togglingId, setTogglingId]     = useState(null);
  const { toasts, show: toast }         = useToast();

  const activeCount   = customizedBanners.filter((i) => toActive(i.status)).length;
  const inactiveCount = customizedBanners.length - activeCount;

  /* ── Filter + Sort ── */
  let filtered = customizedBanners.filter((i) => {
    const matchSearch = (i.description || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      statusFilter === "all"      ? true :
      statusFilter === "active"   ? toActive(i.status) :
      !toActive(i.status);
    return matchSearch && matchStatus;
  });
  if (sortBy === "name_asc")  filtered = [...filtered].sort((a, b) => (a.description || "").localeCompare(b.description || ""));
  if (sortBy === "name_desc") filtered = [...filtered].sort((a, b) => (b.description || "").localeCompare(a.description || ""));
  if (sortBy === "active")    filtered = [...filtered].sort((a, b) => (toActive(b.status) ? 1 : 0) - (toActive(a.status) ? 1 : 0));
  if (sortBy === "inactive")  filtered = [...filtered].sort((a, b) => (toActive(a.status) ? 1 : 0) - (toActive(b.status) ? 1 : 0));

  const { page: custPage, pageSize: custSize, setPage: setCustPage, setPageSize: setCustSize, totalPages: custTotalPages, totalItems: custTotal, paginated: paginatedCust } = usePagination(filtered, 10);

  /* ── Handlers ── */
  const handleAdd = async (formData) => {
    await addCustomizedBanner(formData);
    toast("Customization banner added successfully");
  };

  const handleEdit = async (id, formData) => {
    await updateCustomizedBanner(id, formData);
    toast("Banner updated successfully");
  };

  const handleDelete = async (id) => {
    await deleteCustomizedBanner(id);
    toast("Banner deleted", "warning");
  };

  const handleToggle = async (item) => {
    if (togglingId === item.id) return;
    setTogglingId(item.id);
    try {
      const newStatus = toStatus(!toActive(item.status));
      await updateCustomizedBannerStatus(item.id, newStatus);
      toast(`Banner marked ${newStatus}`);
    } catch (err) {
      toast(err.message || "Failed to update status", "error");
    } finally {
      setTogglingId(null);
    }
  };

  /* ── Loading skeleton ── */
  if (customizedBannersLoading) {
    return (
      <div className="space-y-5 p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="h-20 rounded-2xl bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-3 gap-3">
          {[0,1,2].map((i) => <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
        <div className="h-64 rounded-2xl bg-gray-100 animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
      `}</style>

      <div className="space-y-5 p-4 sm:p-6 max-w-7xl mx-auto">

        {/* ── Stat chips ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-teal-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><Layers className="w-4 h-4 text-teal-700" /></div>
            <div><p className="text-xl font-bold text-teal-700">{customizedBanners.length}</p><p className="text-xs text-gray-500">Total Banners</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-green-500 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><Check className="w-4 h-4 text-green-600" /></div>
            <div><p className="text-xl font-bold text-green-600">{activeCount}</p><p className="text-xs text-gray-500">Active</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 border-l-4 border-l-red-400 p-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-4 h-4 text-red-500" /></div>
            <div><p className="text-xl font-bold text-red-500">{inactiveCount}</p><p className="text-xs text-gray-500">Inactive</p></div>
          </div>
        </div>

        {/* ── Search + Filter ── */}
        {customizedBanners.length > 0 && (
          <div className="rounded-2xl border border-[#e8e0d0] overflow-hidden bg-white">
           <div className="px-4 sm:px-5 py-3 flex items-center gap-3">
  <div className="relative flex-1">
    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    <input
      type="text"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search customization banner…"
      className="w-full pl-9 pr-9 py-2 rounded-lg border border-[#e2ddd4] text-sm bg-white text-gray-700 placeholder-gray-400 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
    />
    {search && (
      <button
        onClick={() => setSearch("")}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
      >
        <X size={13} />
      </button>
    )}
  </div>
  <button
    onClick={() => setFiltersOpen((v) => !v)}
    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all flex-shrink-0
      ${filtersOpen
        ? "bg-teal-50 border-teal-300 text-teal-700"
        : "bg-white border-[#e2ddd4] text-gray-600 hover:border-teal-300 hover:text-teal-700"}`}
  >
    <Filter size={14} />
    <span className="hidden sm:inline">Filters</span>
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}>
      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
</div>

            {/* {filtersOpen && (
              <div className="px-4 sm:px-5 pb-4 pt-0 border-t border-gray-100 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer"
                    >
                      <option value="all">All Banners ({customizedBanners.length})</option>
                      <option value="active">Active ({activeCount})</option>
                      <option value="inactive">Inactive ({inactiveCount})</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sort By</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer"
                    >
                      <option value="">Default</option>
                      <option value="name_asc">Description A–Z</option>
                      <option value="name_desc">Description Z–A</option>
                      <option value="active">Active First</option>
                      <option value="inactive">Inactive First</option>
                    </select>
                  </div>
                </div>
              </div>
            )} */}
          </div>
        )}

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">

          {customizedBanners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-4">
                <Layers size={26} className="text-teal-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No customization banners yet</p>
              {/* <p className="text-xs text-gray-400">Click "+ Add Banner" above to get started</p> */}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Search size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">
                No results for "<span className="font-medium">{search}</span>"
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-700 text-white">
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider w-12 sm:w-16">ID</th>
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider w-24 sm:w-36">Image</th>
                    <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider  w-12 sm:w-16">Description</th>
                   
                    <th className="text-right px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wider  w-12 sm:w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedCust.map((item, idx) => (
                    <tr key={item.id} className={`transition-colors hover:bg-teal-50/40 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>

                      {/* # */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4 text-sm text-gray-500">{idx + 1}</td>

                      {/* Thumbnail */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        <div
                          className="relative w-20 sm:w-28 h-12 sm:h-16 rounded-xl overflow-hidden border border-[#e8e0d0] bg-white cursor-pointer group/img"
                          onClick={() => setViewItem(item)}
                        >
                          <img
                            src={getCustomizedBannerImageUrl(item.image)}
                            alt={item.description}
                            className="w-full h-full object-cover transition-transform duration-200 group-hover/img:scale-105"
                            onError={(e) => { e.target.src = "https://placehold.co/200x100?text=No+Image"; }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/30 transition-all flex items-center justify-center">
                            <ZoomIn size={14} className="text-white opacity-0 group-hover/img:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </td>

                      {/* Description */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        <p className="text-sm font-medium text-gray-800 line-clamp-2 max-w-[140px] sm:max-w-xs">
                          {item.description}
                        </p>
                      
                      </td>


                      {/* Actions */}
                      <td className="px-4 sm:px-5 py-3 sm:py-4">
                        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                          {/* Mobile-only toggle */}
                          <div className="sm:hidden">
                            <StatusToggle
                              active={toActive(item.status)}
                              onChange={() => handleToggle(item)}
                              disabled={togglingId === item.id}
                            />
                          </div>

                          <button
                            onClick={() => setViewItem(item)}
                            title="View banner"
                            className="p-1.5 sm:p-2 rounded-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all active:scale-95"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => setEditItem(item)}
                            title="Edit banner"
                            className="p-1.5 sm:p-2 rounded-lg text-green-500 hover:text-green-700 hover:bg-green-50 transition-all active:scale-95"
                          >
                            <Pencil size={14} />
                          </button>
                         
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {custTotalPages > 1 && (
                <div className="px-4 border-t border-gray-100">
                  <Pagination currentPage={custPage} totalPages={custTotalPages} totalItems={custTotal}
                    pageSize={custSize} onPageChange={setCustPage} onPageSizeChange={setCustSize} />
                </div>
              )}
            </div>
          )}

          {customizedBanners.length > 0 && (
            <div className="px-4 sm:px-5 py-3 border-t border-[#f5f0e8] bg-white flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                Showing {filtered.length} of {customizedBanners.length} banners
              </span>
              {(search || statusFilter !== "all") && filtered.length < customizedBanners.length && (
                <button
                  onClick={() => { setSearch(""); setStatusFilter("all"); }}
                  className="text-[11px] text-teal-600 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* <AddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleAdd}
      /> */}
      <EditModal
        open={!!editItem}
        onClose={() => setEditItem(null)}
        onSave={handleEdit}
        item={editItem}
        getImageUrl={getCustomizedBannerImageUrl}
      />
      <ViewModal
        open={!!viewItem}
        onClose={() => setViewItem(null)}
        item={viewItem}
        getImageUrl={getCustomizedBannerImageUrl}
      />
      <DeleteModal
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={handleDelete}
        item={delItem}
      />
      <ToastContainer toasts={toasts} />
    </>
  );
}